import { useMutation } from '@tanstack/react-query';
import cn from 'classnames';
import { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useMatch, useNavigate } from 'react-router';
import { handleServerFormError, ValidationError } from '#api/errors.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { InlineButton } from '#components/atoms/buttons/inline-button/InlineButton.tsx';
import { Divider } from '#components/atoms/divider/Divider.tsx';
import { ErrorMessage, MFAInput } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { AuthLayout } from '#components/patterns/layouts/auth/AuthLayout.tsx';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useMFAInput } from '#hooks/useMFAInput.ts';
import { useTimeout } from '#hooks/useTimeout.ts';
import { useWebAuthnLogin } from '#screens/auth/login/hooks/useWebAuthnLogin.ts';
import { useMergedRefs } from '#util/mergeRefs.ts';
import { page, Titled } from '#util/title.ts';
import { isWebAuthnSupported } from '#util/webauthn-support.ts';
import styles from './LoginMFA.module.css';

const ENROLLMENT_REQUIRED_CODE = 'ERR_2FA_ENROLLMENT_REQUIRED';
const MFA_LENGTH_ERROR = 'The code must be 6 digits long.';

type FormData = {
  code: string;
};

export function LoginMFA() {
  /** If true, existing auth state is ignored.  Otherwise, calls onSuccess if user is authed */
  const isRenewal = useMatch('/auth/mfa/renew');
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting, isSubmitted, isSubmitSuccessful },
    setValue,
    setError,
    setFocus,
  } = useForm<FormData>({ mode: 'onChange', defaultValues: { code: '' } });
  const { state, inputRef } = useMFAInput<'code'>({
    isSubmitting,
    isSubmitted,
    isSubmitSuccessful,
    setFocus,
    setValue,
    name: 'code',
  });
  const auth = useAuth();
  const userHasMFA = auth.hasUserRegisteredMFA();
  const mutation = useMutation({ mutationFn: auth.loginWithMFA });
  const submitRef = useRef<HTMLInputElement>(null);
  const authState = auth.getAuthState();
  const location = useAppLocation();
  const navigate = useNavigate();
  const redirect = useCallback(
    () => void navigate(location.state?.from ?? '/', { replace: true }),
    [location.state?.from, navigate]
  );
  const { start: redirectAfterDelay } = useTimeout(redirect, 1000);
  const {
    webAuthnMutation,
    webAuthnError,
    clearError: clearWebAuthnError,
  } = useWebAuthnLogin({
    // Redirect immediately: unlike TOTP (where MFAInput shows a success
    // animation during the delay), passkey has no in-page success state, so
    // the delay just lets the button flip from "Waiting for device..." back
    // to "Verify with passkey" before the redirect fires.
    onSuccess: redirect,
  });
  const onEnrollmentRequiredProps = {
    pathname: '/auth/mfa/register',
    state: location.state,
    replace: true,
  };

  // Submit, unless a passkey ceremony is in flight or has succeeded. Password managers such as
  // 1Password fill the TOTP field while the passkey prompt is still completing; submitting then
  // would race the passkey for the session and burn the code. isSuccess is checked separately
  // because isPending clears on success while this screen is still mounted awaiting redirect.
  //
  // Note: the !isSuccess branch is defense-in-depth and is not regression-tested. In practice this
  // screen unmounts on redirect() before query-core dispatches isSuccess: true, so no story can
  // drive the effect through it (see LoginMFA.stories.tsx). Removing it currently breaks nothing;
  // the reconciliation belongs with the MFA state-model rework in DefinedNet/webclient#4157.
  useEffect(() => {
    if (
      isDirty &&
      isValid &&
      !isSubmitting &&
      !isSubmitSuccessful &&
      !webAuthnMutation.isPending &&
      !webAuthnMutation.isSuccess
    ) {
      submitRef.current?.click();
    }
  }, [isValid, isDirty, isSubmitting, isSubmitSuccessful, webAuthnMutation.isPending, webAuthnMutation.isSuccess]);

  // If we're already authed, quickly redirect.
  useEffect(() => {
    if (authState === 'authenticated' && !isSubmitSuccessful && !isRenewal) {
      redirect();
    }
  }, [authState, isSubmitSuccessful, isRenewal, redirect]);

  const submitTOTP = handleSubmit(async (data) => {
    // Avoid double-submission
    if (isSubmitting || isSubmitSuccessful) {
      return;
    }

    try {
      const outcome = await mutation.mutateAsync(data.code);
      // The session was replaced mid-request (e.g. another tab logged in) so nothing was committed.
      // Throw rather than return: a normal return marks the form isSubmitSuccessful, which shows the
      // checkmark and blocks both the redirect effect and any retry.
      if (outcome === 'superseded') {
        throw new ValidationError(
          [{ code: 'ERR_SESSION_CHANGED', message: 'Session changed, please try again.', path: 'code' }],
          new Response()
        );
      }
      redirectAfterDelay();
    } catch (error) {
      // If we got back an enrollment required error, somehow, redirect to the mfa registration page
      if (
        error instanceof ValidationError &&
        error.errors.some((details) => details.code === ENROLLMENT_REQUIRED_CODE)
      ) {
        void navigate(onEnrollmentRequiredProps.pathname, {
          state: onEnrollmentRequiredProps.state,
          replace: onEnrollmentRequiredProps.replace,
        });
        return;
      }
      setValue('code', '');
      setFocus('code');
      handleServerFormError(error, ({ path, message }) => {
        switch (path) {
          case 'code':
            setError(path, { message });
            return true;
          case undefined: // TODO: set a global error
          default:
            return false;
        }
      });
    }
  });

  const { ref, ...inputProps } = register('code', {
    required: 'Enter the 6 digit code from your app.',
    pattern: { value: /[0-9]{6}/, message: 'The 6 digit code is numbers 0-9' },
    minLength: { value: 6, message: MFA_LENGTH_ERROR },
    maxLength: { value: 6, message: MFA_LENGTH_ERROR },
  });

  const mergedRefs = useMergedRefs(ref, inputRef);

  const hasWebAuthn = isWebAuthnSupported() && auth.user?.type === 'user' && auth.user.hasWebAuthnAuthenticator;
  const hasTOTP = auth.user?.type === 'user' && auth.user.hasTOTPAuthenticator;

  if (userHasMFA === 'unknown') return null;

  // Redirect to enrollment if the user has no usable MFA method.
  // This includes users who only have WebAuthn in a browser that doesn't support it.
  if (userHasMFA === 'no' || (!hasWebAuthn && !hasTOTP)) {
    return (
      <Navigate
        to={onEnrollmentRequiredProps.pathname}
        state={onEnrollmentRequiredProps.state}
        replace={onEnrollmentRequiredProps.replace}
      />
    );
  }

  return (
    <Titled title={() => page(isRenewal ? 'Renew MFA' : 'Login')}>
      <AuthLayout
        headerText={
          auth.user ?
            <>
              <span className="truncate">{auth.user.email}</span>
              <span className="shrink-0">
                - <InlineButton onClick={() => auth.signout()}>signout</InlineButton>
              </span>
            </>
          : null
        }
      >
        <div className="simple-page-content">
          <h1 className={cn('h2 mb-24', styles.LoginMFA_heading)}>Verify your account</h1>
          <div className={cn('width-fit-content', styles.LoginMFA_content)}>
            {hasWebAuthn && (
              <div className="mb-24">
                {webAuthnError && <ErrorMessage className="mb-16">{webAuthnError}</ErrorMessage>}
                <Button
                  onClick={() => {
                    clearWebAuthnError();
                    webAuthnMutation.mutate();
                  }}
                  disabled={webAuthnMutation.isPending || isSubmitting || isSubmitSuccessful}
                  className="width-100"
                >
                  {webAuthnMutation.isPending ? 'Waiting for device...' : 'Verify with passkey'}
                </Button>
              </div>
            )}
            {hasWebAuthn && hasTOTP && <Divider className="mb-24">OR</Divider>}
            {hasTOTP && (
              <form method="post" onSubmit={submitTOTP}>
                <Field id="totp-code" label="Enter code from your authenticator app">
                  <MFAInput state={state} {...inputProps} ref={mergedRefs} />
                  {errors.code && isSubmitted && errors.code.message !== MFA_LENGTH_ERROR ?
                    <ErrorMessage className="mt-8">{errors.code.message}</ErrorMessage>
                  : null}
                </Field>
                {/* A disabled sole submit button blocks Enter and makes click() a no-op while the
                    passkey ceremony is pending. It must stay rendered: a single-field form with no
                    submit button still submits on Enter. */}
                <input ref={submitRef} type="submit" hidden={true} disabled={webAuthnMutation.isPending} />
              </form>
            )}
          </div>
        </div>
      </AuthLayout>
    </Titled>
  );
}
